import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopoverListWithFilter from '@shared/components/others/PopoverListWithFilter/PopoverListWithFilter';
import { Chip, Link, Skeleton, TableCell, TableRow } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import { useNavigate } from 'react-router-dom';
import { UI_FUNCTIONAL_TESTING_PATH, UI_TEST_CASE_PATH } from '@shared/constants/pathsConstants';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { selectModulesLookup } from '@pages/modules/store/selectors';
import { getTestCasesRequest, getTestCasesReset, resetTestcasesArr } from '@pages/testcases/store';
import { selectGetTestCasesStatus, selectTestCases, selectTestCasesCount } from '@pages/testcases/store/selectors';
import { useTestcasesChunk } from '@pages/testcases/hooks/useTestcasesChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { toTitleCase } from '@shared/utils/utilsOld';
import { useListState } from '@shared/hooks/useListState';
import useHandleScroll from '@shared/hooks/useHandleScroll';
import { usePrevious } from '@shared/hooks/usePrevious';

const enableInfiniteScroll = true;

const TestCasesPopover = (props) => (
    <ComponentWithChunks
        featuresArray={['testCases']}
        hooksArray={[useTestcasesChunk]}
        Component={TestCasesPopoverComponent}
        {...props}
    />
);

const TestCasesPopoverComponent = ({
    testcasesPopoverAnchorEl,
    setTestcasesPopoverAnchorEl,
    testplanId = undefined,
    schemeId,
}) => {
    const { t } = useTranslation();
    const testCases = useSelector(selectTestCases);
    const getTestCasesStatus = useSelector(selectGetTestCasesStatus);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const modulesLookup = useSelector(selectModulesLookup);
    const { globalFilter, setGlobalFilter, pagination, setPagination, fetchData } = useListState(
        'testcasesPopover',
        false,
        getTestCasesRequest,
        { disableUrlParams: true, testplanId, enableInfiniteScroll, schemeId },
    );
    const prevGlobalFilter = usePrevious(globalFilter);
    const { pageIndex, pageSize } = pagination;
    const testcasesCount = useSelector(selectTestCasesCount);
    const allTestcasesFetched = (pageIndex + 1) * pageSize >= testcasesCount;
    const isLoading = getTestCasesStatus === REQUEST_STATUS.LOADING;

    const handleScroll = useHandleScroll({ isLoading, setPagination, allItemsFetched: allTestcasesFetched });

    useEffect(() => {
        dispatch(resetTestcasesArr());
    }, [dispatch]);

    useEffect(() => {
        if (testcasesPopoverAnchorEl && (testplanId || schemeId)) {
            fetchData();
        }
        if (!testcasesPopoverAnchorEl) {
            dispatch(resetTestcasesArr());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeId, testcasesPopoverAnchorEl, testplanId]);

    useEffect(() => {
        if (
            (globalFilter !== '' && globalFilter !== prevGlobalFilter) || // global filter changed
            (globalFilter === '' && prevGlobalFilter !== undefined && globalFilter !== prevGlobalFilter) // global filter was set to empty string
        ) {
            dispatch(getTestCasesReset());
        }
    }, [dispatch, globalFilter, prevGlobalFilter]);

    const handleNavigateToTestcase = (testcaseId) => (e) => {
        e.preventDefault();
        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testcaseId}`);
    };

    return (
        <PopoverListWithFilter
            anchorEl={testcasesPopoverAnchorEl}
            setAnchorEl={setTestcasesPopoverAnchorEl}
            data={testCases}
            dataFilterFields={['pk', 'name']}
            renderValue={(testcase) => {
                const testcaseId = testcase.pk;
                const moduleName = modulesLookup[testcase.module]?.name;
                return (
                    <TableRow key={testcaseId}>
                        <TableCell size="small">{testcase.pk}</TableCell>
                        <TableCell size="small" sx={{ wordBreak: 'break-all' }}>
                            <Link
                                underline="hover"
                                onClick={handleNavigateToTestcase(testcaseId)}
                                href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testcaseId}`}
                                sx={{ cursor: 'pointer' }}
                            >
                                {testcase.name}
                            </Link>
                        </TableCell>
                        <TableCell size="small" sx={{ whiteSpace: 'nowrap' }}>
                            {testcase.is_handtest ? t('hand-test') : t('auto-test')}
                        </TableCell>
                        <TableCell size="small">
                            <Chip
                                label={
                                    <OverflowTooltip title={moduleName || t('missing')}>
                                        {moduleName || t('missing')}
                                    </OverflowTooltip>
                                }
                                size="small"
                                sx={{ maxWidth: 250 }}
                            />
                        </TableCell>
                    </TableRow>
                );
            }}
            table={{ columns: ['ID', toTitleCase(t('test_case')), t('type'), t('module')] }}
            title={t('test_cases')}
            isLoading={isLoading}
            enableInfiniteScroll={enableInfiniteScroll}
            Skeleton={TestCasesSkeleton}
            handleScroll={handleScroll}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
        />
    );
};

const TestCasesSkeleton = () => {
    const SKELETONS_COUNT = 10;

    return [...Array(SKELETONS_COUNT)].map((item) => (
        <TableRow key={item}>
            <TableCell size="small">
                <Skeleton animation="wave" />
            </TableCell>
            <TableCell size="small">
                <Skeleton animation="wave" />
            </TableCell>
            <TableCell size="small">
                <Skeleton animation="wave" />
            </TableCell>
            <TableCell size="small">
                <Skeleton animation="wave" />
            </TableCell>
        </TableRow>
    ));
};

export default TestCasesPopover;
